<template>
  <v-card outlined class="agent-brief">
    <pre class="d-none">{{ payload }}</pre>
    <v-card-title class="pb-2 pt-3 px-3 d-flex flex-wrap">
      {{ payload.localBrief.short_title }}
      <v-chip v-if="payload.localBrief.project.domain" small color="indigo" dark class="mr-2 mb-1 ml-2">
        {{ payload.localBrief.project.domain }}
      </v-chip>
      <v-chip v-if="payload.localBrief.mode" small outlined color="blue-grey" class="mb-1">
        {{ payload.localBrief.mode }}
      </v-chip>
    </v-card-title>

    <v-window v-model="step">
      <v-window-item :value="1">
        <div class="px-3 pb-2">
          <DimensionCard
            v-if="payload.localBrief.project.dimensions"
            v-model="payload.localBrief.project.dimensions"
            class="mb-3"
          />
          <ProjectCard
            v-model="payload.localBrief.project"
            class="mb-3"
          />
        </div>
      </v-window-item>

      <v-window-item :value="0">
        <div class="px-3 pb-2">
          <QuestionsCard
            :questions="pendingQuestions"
            :assumed-questions="assumedQuestions"
            :assumptions="payload.localBrief.project.assumptions || []"
            v-model="localAnswers"
            class="mb-2"
            @edit-assumption="promoteToStack"
          />
        </div>
      </v-window-item>
    </v-window>

    <div class="d-flex justify-center py-1">
      <v-icon
        v-for="i in 2"
        :key="i"
        small
        :color="step === i - 1 ? 'primary' : 'grey lighten-2'"
        class="mx-1 d-none"
        style="cursor:pointer"
        @click="step = i - 1"
      >mdi-circle</v-icon>
    </div>

    <v-card-actions class="px-3 pb-3 pt-0">
      <template v-if="step === 0">
        <v-btn color="secondary" outlined block @click="step = 1">
          OK
          <v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <v-btn text @click="step = 0">
          <v-icon left small>mdi-chevron-left</v-icon>
          Retour
        </v-btn>
        <v-spacer />
        <v-btn color="primary" @click="confirm">
          <v-icon left small>mdi-check-circle-outline</v-icon>
          Confirmer et envoyer
        </v-btn>
      </template>
    </v-card-actions>
  </v-card>
</template>

<script>
import DimensionCard from './DimensionCard.vue'
import ProjectCard from './ProjectCard.vue'
import QuestionsCard from './QuestionsCard.vue'

const CONFIDENCE_THRESHOLD = 0.8

export default {
  name: 'AgentBrief',
  components: { DimensionCard, ProjectCard, QuestionsCard },
  props: {
    brief: { type: Object, required: true },
  },
  data() {
    // Deep copy — edits must not mutate the original message content
    const b = JSON.parse(JSON.stringify(this.brief))
    const allQuestions = b.project?.questions || []
    const initialAnswers = Object.fromEntries(
      allQuestions.map(q => [q.key, q.default_value ?? ''])
    )
    return {
      step: 0,
      originalBrief: JSON.parse(JSON.stringify(b)),
      originalAnswers: { ...initialAnswers },
      payload: {
        localBrief: b,
        changes: [],
      },
      assumedQuestions: allQuestions.filter(q => (q.confidence ?? 0.5) >= CONFIDENCE_THRESHOLD),
      pendingQuestions: allQuestions.filter(q => (q.confidence ?? 0.5) < CONFIDENCE_THRESHOLD),
      // Pre-seed all keys (assumed + pending) so promoted questions have initial values
      localAnswers: initialAnswers,
    }
  },
  watch: {
    'payload.localBrief': { deep: true, handler: 'syncChanges' },
    localAnswers: { deep: true, handler: 'syncChanges' },
  },
  methods: {
    flatDiff(current, original, prefix = '') {
      const changes = []
      const keys = new Set([...Object.keys(current || {}), ...Object.keys(original || {})])
      for (const key of keys) {
        if (key === 'questions') continue
        const path = prefix ? `${prefix}.${key}` : key
        const cv = (current || {})[key]
        const ov = (original || {})[key]
        if (cv !== null && cv !== undefined && typeof cv === 'object' && !Array.isArray(cv)) {
          changes.push(...this.flatDiff(cv, ov, path))
        } else if (JSON.stringify(cv) !== JSON.stringify(ov)) {
          changes.push({ field: path, value: cv })
        }
      }
      return changes
    },
    syncChanges() {
      const briefChanges = this.flatDiff(this.payload.localBrief, this.originalBrief)
      const answerChanges = Object.entries(this.localAnswers)
        .filter(([k, v]) => v !== this.originalAnswers[k])
        .map(([k, v]) => ({ field: `answers.${k}`, value: v }))
      this.payload.changes = [...briefChanges, ...answerChanges]
    },
    promoteToStack(q) {
      this.assumedQuestions = this.assumedQuestions.filter(aq => aq.key !== q.key)
      this.pendingQuestions = [...this.pendingQuestions, q]
      this.payload.changes.push({ field: `hypothesis.${q.key}`, value: q.default_value ?? '' })
    },
    confirm() {
      this.$emit('confirm', JSON.stringify(this.payload))
    },
  },
}
</script>

<style scoped>
.agent-brief {
  background: var(--app-card-bg, #f8f9ff);
  border-color: var(--app-card-border, #c5cae9);
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: scale(0.93) translateY(6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.agent-brief > * {
  animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.agent-brief > *:nth-child(1) { animation-delay: 0.25s; }
.agent-brief > *:nth-child(2) { animation-delay: 0.9s; }
.agent-brief > *:nth-child(3) { animation-delay: 1.55s; }
.agent-brief > *:nth-child(4) { animation-delay: 2.2s; }

/* DimensionCard — 3 input columns */
::v-deep .dimension-card .col:nth-child(1) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 2.0s; }
::v-deep .dimension-card .col:nth-child(2) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 2.2s; }
::v-deep .dimension-card .col:nth-child(3) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 2.4s; }

/* ProjectCard — description, usage, constraints label, feu, acoustique, humidite */
::v-deep .project-card .col:nth-child(1) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 2.6s; }
::v-deep .project-card .col:nth-child(2) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 2.8s; }
::v-deep .project-card .col:nth-child(3) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 3.0s; }
::v-deep .project-card .col:nth-child(4) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 3.2s; }
::v-deep .project-card .col:nth-child(5) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 3.4s; }
::v-deep .project-card .col:nth-child(6) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 3.6s; }

/* QuestionsCard — each question row */
::v-deep .questions-card .mb-3:nth-child(1) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 2.0s; }
::v-deep .questions-card .mb-3:nth-child(2) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 2.2s; }
::v-deep .questions-card .mb-3:nth-child(3) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 2.4s; }
::v-deep .questions-card .mb-3:nth-child(4) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 2.6s; }
::v-deep .questions-card .mb-3:nth-child(5) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 2.8s; }
::v-deep .questions-card .mb-3:nth-child(6) { animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: 3.0s; }
</style>
